// @ts-nocheck
"use client"

import React, { useMemo } from "react"
import { X } from "lucide-react"

// Local boundary so a throw inside the EQ panel degrades to a readable message
// instead of blanking the entire analysis page. Full detail goes to the console
// for debugging; the user sees a calm fallback.
class PanelErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(error) { return { error } }
  componentDidCatch(error, info) { console.error("[EQ panel] render error:", error, info?.componentStack) }
  render() {
    if (this.state.error) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
          <p className="text-sm font-medium text-muted-foreground">This analysis view couldn&apos;t be displayed.</p>
          <p className="text-xs text-muted-foreground/70">Try refreshing — the rest of the deal is unaffected.</p>
        </div>
      )
    }
    return this.props.children
  }
}
import { Button } from "@/components/ui/Button"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tabs, TabsList, TabsTab, TabsPanel } from "@/components/ui/tabs"
import { ChatPanel } from "./chat-panel"
import { CommentsPanel } from "./comments-panel"
import { IqQuestionsPanel, getIqQuestionCount } from "./iq-questions-panel"
import { CitationsPanel } from "./citations-panel"
import { ExtractedEvidencePanel } from "../ExtractedEvidencePanel"
import { useSidebarContext } from "../contexts/activity-sidebar-context"
import SportsContractCardsDemo from "../SportsContractCardsDemo"
import type { GetContractResponseContent } from "src/types/contract.types"

interface ActivitySidebarProps {
  contract?: GetContractResponseContent
  onIqTermSelect?: (termWithInstance: string) => void
  highlightedTerm?: string | null
  onCitationTermSelect?: (termWithInstance: string) => void
  taggedContractText?: string
  /**
   * Demo route keeps the legacy variables-driven CitationsPanel (its mock
   * data populates contractExtraction.variables). The REAL route swaps in
   * the eqCardsRaw-driven ExtractedEvidencePanel.
   */
  isDemo?: boolean
  /** Jump the PDF viewer to a 1-based page (from a citation click). */
  onJumpToPage?: (page: number) => void
}

export function ActivitySidebar({
  contract,
  onIqTermSelect,
  highlightedTerm,
  onCitationTermSelect,
  taggedContractText,
  isDemo = false,
  onJumpToPage,
}: ActivitySidebarProps) {
  const { sidebarTab, closeSidebar, setSidebarTab } = useSidebarContext()
  const iqQuestionCount = useMemo(() => getIqQuestionCount(contract), [contract])

  return (
    <div className="h-full border-l bg-background flex flex-col">
      <div className="flex h-14 items-center justify-between border-b px-4 shrink-0">
        <h2 className="font-semibold">Activity</h2>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={closeSidebar}
        >
          <X className="size-4" />
        </Button>
      </div>

      <Tabs 
        value={sidebarTab} 
        onValueChange={setSidebarTab}
        className="flex flex-col flex-1 min-h-0 overflow-hidden"
      >
        <div className="px-4 pt-4 shrink-0">
          <div className="scroll-mask-x overflow-y-visible pb-1 [--scroll-mask-size:24px]">
            <TabsList className="min-w-max" variant="pill" indicatorColor="secondary">
              <TabsTab value="cards">EQ</TabsTab>
              <TabsTab value="iq">
                IQ
                <Badge variant="success" className="ml-1.5 text-[8px]!">{iqQuestionCount}</Badge>
              </TabsTab>
              {/* <TabsTab value="chat">Chat</TabsTab> */}
              {/* <TabsTab value="comments">
                Comments <Badge variant="secondary" className="ml-1.5">4</Badge>
              </TabsTab> */}
              {/* <TabsTab value="activity">Activity</TabsTab> */}
              <TabsTab value="citations">Citations</TabsTab>
            </TabsList>
          </div>
        </div>

        <TabsPanel value="chat" className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <ChatPanel />
        </TabsPanel>

        <TabsPanel value="comments" className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <CommentsPanel />
        </TabsPanel>

        <TabsPanel value="activity" className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <ScrollArea className="flex-1">
            <div className="px-4 py-4">
              <p className="text-sm text-muted-foreground">No activity yet</p>
            </div>
          </ScrollArea>
        </TabsPanel>

        <TabsPanel value="cards" className="flex-1 flex flex-col min-h-0 overflow-auto">
          <PanelErrorBoundary>
            <SportsContractCardsDemo embedded contract={contract} demoFallback={isDemo} />
          </PanelErrorBoundary>
        </TabsPanel>

        <TabsPanel value="iq" className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <IqQuestionsPanel contract={contract} onTermSelect={onIqTermSelect} />
        </TabsPanel>

        <TabsPanel value="citations" className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {isDemo ? (
            <CitationsPanel
              contract={contract}
              highlightedTerm={highlightedTerm}
              onTermSelect={onCitationTermSelect}
              taggedContractText={taggedContractText}
            />
          ) : (
            <ExtractedEvidencePanel
              contract={contract}
              onJumpToPage={onJumpToPage}
            />
          )}
        </TabsPanel>
      </Tabs>
    </div>
  )
}
